import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { LayoutAwareForm } from '@/components/crm/layout-aware-form'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/opportunities/$opportunityId/edit')({
  component: EditOpportunityPage,
})

/**
 * NOTE: The backend `crm.opportunities.update` procedure explicitly
 * rejects changes to `stageId` — that transition must go through
 * `crm.opportunities.changeStage` so derived fields (stage-enter time,
 * forecasting buckets, automation triggers) are applied. This edit
 * form deliberately does not drive stage; the detail page exposes a
 * stage dropdown that calls the correct mutation.
 */
function EditOpportunityPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { opportunityId } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.opportunities.get.queryOptions({ input: { id: opportunityId } }),
  )

  const updateMutation = useMutation(
    orpc.crm.opportunities.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        queryClient.invalidateQueries({ queryKey: orpc.crm.opportunities.list.key() })
        queryClient.invalidateQueries({
          queryKey: orpc.crm.opportunities.get.queryKey({ input: { id: opportunityId } }),
        })
        navigate({
          to: '/crm/opportunities/$opportunityId',
          params: { opportunityId },
        })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  if (isLoading || !data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate({
              to: '/crm/opportunities/$opportunityId',
              params: { opportunityId },
            })
          }
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
        <h1 className="text-2xl font-bold">
          {t('crm.common.edit')} {t('crm.opportunity.entity').toLowerCase()}
        </h1>
      </div>
      <LayoutAwareForm
        entityKey="opportunity"
        initial={data as Record<string, unknown>}
        onSubmit={(value) => {
          // Defensive strip: if the layout ever exposed stage as an
          // editable input, we still must not send it through `update`.
          const { stageId: _stageId, ...rest } = value as Record<string, unknown>
          updateMutation.mutate({ id: opportunityId, ...rest } as Parameters<
            typeof updateMutation.mutate
          >[0])
        }}
        submitting={updateMutation.isPending}
        submitLabel={t('crm.common.save')}
        onCancel={() =>
          navigate({
            to: '/crm/opportunities/$opportunityId',
            params: { opportunityId },
          })
        }
      />
    </div>
  )
}
